import React from 'react';
import { useTranslation } from 'react-i18next';

import { IDatepickerChildrenProps } from '@private/datepicker';
import { useUpdateEffect } from '@private/hooks';
import { format } from 'date-fns/format';
import { formatInTimeZone } from 'date-fns-tz';

import Input from 'components/Controls/Input';
import Datepicker, { IDatepicker } from 'components/Datepicker';
import {
  DATE_MASK,
  DATE_TIME_MASK,
  LOCAL_DATE_MASK,
  TIMEZONE_MASK,
} from 'constants/common';
import { appReactMemo } from 'hocs';
import { useDateLocale } from 'hooks/useDateLocale';
import { getUtcDate } from 'utils/common';

export interface IDatePickerInput extends Omit<IDatepicker, 'children'> {
  disabled?: boolean;
  endIcon?: React.ReactElement;
  error?: boolean;
  errorMessage?: string;
  fieldRef?: React.Ref<HTMLInputElement>;
  hideInput?: boolean;
  hintText?: string;
  iconSize?: number;
  inputMask?: string;
  isAnimatedLabel?: boolean;
  isAutoFocused?: boolean;
  label?: string;
  maxHintWidth?: number;
  name?: string;
  onBlur?: () => void;
  placeholder?: string;
  showError?: boolean;
  sizeVariant?: 'lg' | 'md' | 'sm';
  startIcon?: React.ReactElement;
  testIds?: IDatePickerTestIds;
  timezoneMask?: string;
  value?: string | TDateValue;
}

export interface IDatePickerTestIds {
  input?: string;
}

export type TDateValue = { endDate: string; startDate: string };

const ZONED_VALUE_REGEX = /(Z|[+-]\d{2}:\d{2})$/;

interface IInputComponent
  extends Omit<IDatePickerInput, 'onChange'>,
    Partial<IDatepickerChildrenProps> {
  hideTime?: boolean;
}

const InputComponent = ({
  disabled,
  endDate,
  error,
  errorMessage,
  hideTime,
  initialTimeZone,
  inputMask,
  isAnimatedLabel,
  label,
  placeholder,
  setEndDate,
  setStartDate,
  showError,
  sizeVariant = 'lg',
  startDate,
  testIds,
  value,
  ...rest
}: IInputComponent) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'common.datepicker.datepickerInput',
  });

  const locale = useDateLocale();

  const calendarValue = value as TDateValue | undefined;

  useUpdateEffect(() => {
    const mask = hideTime ? LOCAL_DATE_MASK : TIMEZONE_MASK;

    if (!startDate) {
      setStartDate?.({ format: mask, value: calendarValue?.startDate || null });
    }

    if (!endDate) {
      setEndDate?.({ format: mask, value: calendarValue?.endDate || null });
    }
  }, [calendarValue]);

  useUpdateEffect(() => {
    if (typeof calendarValue === 'object' && !calendarValue?.startDate) {
      setStartDate?.({ value: null });
    }
  }, [calendarValue?.startDate]);

  useUpdateEffect(() => {
    if (typeof calendarValue === 'object' && !calendarValue?.endDate) {
      setEndDate?.({ value: null });
    }
  }, [calendarValue?.endDate]);

  const getValue = () => {
    if (!calendarValue) return '';
    if (typeof calendarValue === 'string') {
      const isDateStart =
        hideTime ||
        Number(new Date(calendarValue)) === new Date(calendarValue).setHours(0, 0, 0, 0);

      const mask = inputMask || (isDateStart ? DATE_MASK : DATE_TIME_MASK);

      // only a value with an explicit offset is a real instant that can be shown in another zone
      if (initialTimeZone && ZONED_VALUE_REGEX.test(calendarValue)) {
        return formatInTimeZone(new Date(calendarValue), initialTimeZone, mask, {
          locale,
        });
      }

      return format(getUtcDate(calendarValue), mask, { locale });
    }

    const fromTimeStart =
      hideTime ||
      Number(new Date(calendarValue.startDate)) ===
        new Date(calendarValue.startDate).setHours(0, 0, 0, 0);
    const toTimeStart =
      hideTime ||
      Number(new Date(calendarValue.endDate)) ===
        new Date(calendarValue.endDate).setHours(0, 0, 0, 0);

    const getFormattedInputDate = () => {
      const startDate = new Date(calendarValue.startDate);
      const endDate = new Date(calendarValue.endDate);
      const startDateMask = inputMask || (fromTimeStart ? DATE_MASK : DATE_TIME_MASK);
      const endDateMask = inputMask || (toTimeStart ? DATE_MASK : DATE_TIME_MASK);

      if (initialTimeZone) {
        return `${formatInTimeZone(startDate, initialTimeZone, startDateMask, {
          locale,
        })} - ${formatInTimeZone(endDate, initialTimeZone, endDateMask, { locale })}`;
      }

      return `${format(startDate, startDateMask, { locale })} - ${format(
        endDate,
        endDateMask,
        { locale },
      )}`;
    };

    return getFormattedInputDate();
  };

  return (
    <Input
      {...rest}
      disabled={disabled}
      error={error}
      errorMessage={errorMessage}
      isAnimatedLabel={isAnimatedLabel}
      label={label}
      placeholder={placeholder || t('placeholder')}
      readOnly
      showError={showError}
      sizeVariant={sizeVariant}
      testIds={{ input: testIds?.input }}
      value={getValue()}
    />
  );
};

// Date-only values (`yyyy-MM-dd`) carry no UTC offset, so the calendar has to be seeded with an
// already parsed Date. Handing it `{ value, format }` makes it parse a *local* midnight and then
// re-zone that into `initialTimeZone`, which preselects the previous day for browsers east of UTC.
const getInitialDateProps = (value: string, mask: string, hideTime?: boolean) => {
  if (!value) {
    return { format: mask, value: null };
  }

  return hideTime ? { date: getUtcDate(value) } : { format: mask, value };
};

const DatePickerInput = ({
  className,
  disabled,
  endIcon,
  error,
  errorMessage,
  fieldRef,
  hideInput,
  hintText,
  iconSize,
  inputMask,
  isAnimatedLabel = false,
  label,
  maxHintWidth,
  name,
  onBlur,
  placeholder,
  showError,
  sizeVariant = 'lg',
  startIcon,
  testIds,
  timezoneMask = TIMEZONE_MASK,
  value,
  ...restProps
}: IDatePickerInput) => {
  const { hideTime } = restProps.settingsConfig || {};

  const initialDate = React.useMemo(() => {
    if (!value) return;

    if (typeof value === 'string') {
      return { start: getInitialDateProps(value, timezoneMask, hideTime) };
    }

    return {
      end: getInitialDateProps(value.endDate, timezoneMask, hideTime),
      start: getInitialDateProps(value.startDate, timezoneMask, hideTime),
    };
  }, [hideTime, value, timezoneMask]);

  const inputProps = {
    className,
    disabled,
    endIcon,
    error,
    errorMessage,
    hideTime,
    hintText,
    iconSize,
    initialTimeZone: restProps.initialTimeZone,
    inputMask,
    isAnimatedLabel,
    label,
    maxHintWidth,
    name,
    onBlur,
    placeholder,
    showError,
    sizeVariant,
    startIcon,
    testIds,
    value,
  };

  if (disabled) {
    return <InputComponent {...inputProps} />;
  }

  if (hideInput) {
    return <Datepicker {...restProps} initialDate={initialDate} />;
  }

  return (
    <Datepicker {...restProps} initialDate={initialDate}>
      {(datepickerProps) => {
        return (
          <div ref={fieldRef}>
            <InputComponent {...inputProps} {...datepickerProps} />
          </div>
        );
      }}
    </Datepicker>
  );
};

export default appReactMemo(DatePickerInput);
